/**
 * ダッシュボードのタブと URL の対応。
 *
 * タブのキーをそのままパスに使う（`/dashboard/connection` など）。
 * 別の対応表を作らないので、タブを増やしたときに片方だけ直す事故が起きない。
 *
 * 旧形式の `?tab=` も受け付ける。Stripe の戻り先が
 * `/dashboard?tab=billing&status=success` で設定されており、
 * これを読めていなかったため決済後にダッシュボードへ戻っていた。
 */

export type Tab =
  | "dashboard"
  | "logs"
  | "connection"
  | "users"
  | "hubspot-users"
  | "settings"
  | "billing";

export const TAB_KEYS: Tab[] = [
  "dashboard",
  "logs",
  "connection",
  "users",
  "hubspot-users",
  "settings",
  "billing",
];

export function isTab(v: string): v is Tab {
  return (TAB_KEYS as string[]).includes(v);
}

/** ダッシュボード配下のパスか（`/` と `/dashboard...` を含む） */
export function isDashboardPath(pathname: string): boolean {
  return pathname === "/" || pathname === "" || pathname === "/dashboard" || pathname.startsWith("/dashboard/");
}

export interface ParsedRoute {
  tab: Tab;
  /** 旧形式（`?tab=`）で来たか。true なら新パスへ置き換える */
  legacy: boolean;
  /** Stripe からの戻り。`status=success` のとき決済完了を表示する */
  status: string | null;
}

/**
 * URL からタブを決める。
 *
 * 優先順位はパス → クエリ。両方あればパスを採る（パスが正）。
 * 未知の値はダッシュボードに寄せる（存在しないタブで白画面にしない）。
 */
export function parseRoute(pathname: string, search: string): ParsedRoute {
  const params = new URLSearchParams(search);
  const status = params.get("status");

  if (pathname.startsWith("/dashboard/")) {
    const seg = pathname.slice("/dashboard/".length).split("/")[0] ?? "";
    return { tab: isTab(seg) ? seg : "dashboard", legacy: false, status };
  }

  const queryTab = params.get("tab");
  if (queryTab && isTab(queryTab)) {
    return { tab: queryTab, legacy: true, status };
  }

  return { tab: "dashboard", legacy: false, status };
}

/** タブに対応するパス。ダッシュボードだけは `/dashboard` にする */
export function pathForTab(tab: Tab): string {
  return tab === "dashboard" ? "/dashboard" : `/dashboard/${tab}`;
}

/**
 * ロールで開けないタブを寄せる。
 *
 * admin は請求タブを表示しないため、直接 URL を開かれても
 * 何も表示されない画面にならないようダッシュボードへ寄せる。
 */
export function resolveTabForRole(tab: Tab, role: string | undefined): Tab {
  if (tab === "billing" && role === "admin") return "dashboard";
  return tab;
}
